import type { FlatAttribution } from "@src/funnel/flattenAttribution";
import type { FunnelDraftAnswers } from "@src/funnel/useFunnelDraft";

/**
 * Attribution fields mirrored into the answer bag under a `__` prefix so that
 * config-only steps (`value_map` inputs, `{{…}}` templates, branching
 * conditions) can key off the inbound ad without any runtime code.
 *
 * Example: an ad landing with `?utm_content=SM-ADELIN-25YONAUGHTY-01` exposes
 * `answers.__utm_content === "SM-ADELIN-25YONAUGHTY-01"`.
 */
export const ATTRIBUTION_ANSWER_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "campaign_id",
  "adset_id",
  "ad_id",
  "placement",
  "network",
  "inlet_id",
] as const satisfies ReadonlyArray<keyof FlatAttribution>;

export const attributionAnswerKey = (field: (typeof ATTRIBUTION_ANSWER_FIELDS)[number]): string =>
  `__${field}`;

/**
 * True when a resumed draft was captured under a different ad than the
 * current attribution, so the mapping steps must re-run:
 *  - a `__` key already in the draft now carries a different non-empty value, or
 *  - the draft has no value for a click field that is set now *and* the last
 *    touch was captured after the draft was saved (organic draft, ad arrived
 *    later). Drafts saved before mirroring existed have an older last touch
 *    and resume normally, as do plain reloads and fields the envelope lacks.
 */
export const attributionChangedSinceDraft = (
  draftAnswers: FunnelDraftAnswers,
  attribution: FlatAttribution | undefined,
  draftSavedAt?: number,
): boolean => {
  if (attribution === undefined) return false;
  const lastTouchAt = attribution.last_touch_at ? Date.parse(attribution.last_touch_at) : NaN;
  const clickAfterDraft =
    draftSavedAt !== undefined && Number.isFinite(lastTouchAt) && lastTouchAt > draftSavedAt;
  return ATTRIBUTION_ANSWER_FIELDS.some((field) => {
    const value = attribution[field];
    if (typeof value !== "string" || value === "") return false;
    const prev = draftAnswers[attributionAnswerKey(field)];
    if (typeof prev === "string" && prev !== "") return prev !== value;
    return clickAfterDraft && field !== "inlet_id";
  });
};

/**
 * Returns `answers` with the `__`-prefixed keys made equal to the current
 * attribution: present fields are written, absent ones removed. Attribution
 * always wins over a stale value from a resumed draft (a new ad click must
 * re-map). Returns the same object when nothing changes so React state stays
 * stable.
 */
export const seedAttributionAnswers = (
  answers: FunnelDraftAnswers,
  attribution: FlatAttribution | undefined,
): FunnelDraftAnswers => {
  let next: FunnelDraftAnswers | null = null;
  for (const field of ATTRIBUTION_ANSWER_FIELDS) {
    const raw = attribution?.[field];
    const value = typeof raw === "string" && raw !== "" ? raw : undefined;
    const key = attributionAnswerKey(field);
    if (value === undefined ? !(key in answers) : answers[key] === value) continue;
    next ??= { ...answers };
    if (value === undefined) delete next[key];
    else next[key] = value;
  }
  return next ?? answers;
};
